// Gestione dopo la cottura nella scheda ricetta e nella compilazione in
// blocco: le 8 schede, i parametri della modalità scelta, la valutazione HACCP,
// le fasi successive (es. rigenerazione) e il suggerimento del software.
// Tutti i parametri sono facoltativi: obbligatoria è solo la scelta della
// modalità e, per quelle che lo richiedono, la motivazione per approvare.
import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, Lightbulb, Plus, Trash2, ArrowDown, ArrowUp, Info } from "lucide-react";
import api from "../lib/api";
import { Field, FieldHelp, Input, Select, inputCls } from "./ui-kit";
import { POST_COOKING_MODES, MODE_BY_KEY, POST_COOKING_LABELS, ASSESSMENT_FIELDS, PHASE_FIELDS, RAW_STATES, RAW_PROCESS_FIELDS, SHELF_LIFE_INFO_FIELDS, DATE_INDICATIONS, AMBIENT_PROCEDURE_TEXT, modeNeedsBlast } from "../lib/postCookingModes";

const TONES = {
  emerald: "bg-emerald-100 text-emerald-700", sky: "bg-sky-100 text-sky-700", indigo: "bg-indigo-100 text-indigo-700",
  violet: "bg-violet-100 text-violet-700", amber: "bg-amber-100 text-amber-700", orange: "bg-orange-100 text-orange-700",
  rose: "bg-rose-100 text-rose-700", slate: "bg-slate-200 text-slate-700",
};

export function OptionalTag() {
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Facoltativo</span>;
}

function FieldControl({ field, value, onChange, testId }) {
  if (field.type === "bool") {
    return (
      <label className="flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border border-hairline bg-white p-3 text-sm text-navy sm:col-span-2">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-indigo-600" data-testid={testId} />
        <span>{field.label}</span>
      </label>
    );
  }
  if (field.type === "select") {
    return <Field label={field.label}><Select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>{field.options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>;
  }
  if (field.type === "textarea") {
    return (
      <div className="sm:col-span-2">
        <Field label={field.label}><textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={`${inputCls} min-h-[88px]`} data-testid={testId} /></Field>
      </div>
    );
  }
  return (
    <Field label={field.unit ? `${field.label} (${field.unit})` : field.label}>
      <Input type={field.type === "number" ? "number" : "text"} step={field.type === "number" ? "any" : undefined} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} data-testid={testId} />
    </Field>
  );
}

function FieldGrid({ fields, values, onChange, testPrefix }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields.map((f) => <FieldControl key={f.key} field={f} value={values?.[f.key]} onChange={(v) => onChange({ ...(values || {}), [f.key]: v })} testId={`${testPrefix}-${f.key}`} />)}
    </div>
  );
}

// Sezione richiudibile: i campi facoltativi non appesantiscono il modulo.
function Collapsible({ title, badge, defaultOpen = false, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-3 rounded-xl border border-hairline bg-white" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex min-h-[44px] w-full items-center justify-between gap-3 px-4 py-2.5 text-left" aria-expanded={open}>
        <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-navy">{title}{badge}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t border-hairline px-4 pb-4 pt-3">{children}</div>}
    </div>
  );
}

export function PostCookingCards({ value, onChange, suggestion }) {
  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Gestione dopo la cottura">
      {POST_COOKING_MODES.map((m) => {
        const active = value === m.key;
        const Icon = m.icon;
        return (
          <div key={m.key} className={`relative flex rounded-xl border-2 transition-all ${active ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200" : `border-hairline bg-white hover:border-indigo-300 ${!value ? "animate-pulse ring-2 ring-amber-300 ring-offset-1" : ""}`}`}>
            <button type="button" role="radio" aria-checked={active} onClick={() => onChange(m.key)} className="flex w-full items-start gap-3 p-3 pr-9 text-left" data-testid={`ricetta-post-cooking-${m.key}`}>
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${TONES[m.tone]}`}><Icon className="h-5 w-5" /></span>
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-navy">{active && <CheckCircle2 className="h-4 w-4 shrink-0 text-indigo-600" />}{m.label}</span>
                {suggestion === m.key && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"><Lightbulb className="h-3 w-3" /> Suggerita dal software</span>}
                <span className="mt-1 block text-xs leading-relaxed text-slate-500">{m.desc}</span>
                {m.caution && <span className="mt-1.5 flex items-start gap-1 text-[11px] leading-snug text-amber-800"><AlertTriangle className="mt-px h-3 w-3 shrink-0" />{m.caution}</span>}
              </span>
            </button>
            {m.help && <span className="absolute right-2 top-2"><FieldHelp text={m.help} /></span>}
          </div>
        );
      })}
    </div>
  );
}

// Suggerimento e criticità calcolati dal server (post_cooking_check.py).
export function usePostCookingCheck(draft, enabled = true) {
  const [result, setResult] = useState(null);
  const key = JSON.stringify(draft);
  useEffect(() => {
    if (!enabled) { setResult(null); return undefined; }
    const timer = setTimeout(() => {
      api.post("/app/catalog/recipes/post-cooking-check", draft).then(({ data }) => setResult(data)).catch(() => setResult(null));
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);
  return result;
}

export function SuggestionBox({ check, value, onUse }) {
  if (!check) return null;
  const { suggestion, suggestion_reason: reason, flags = [], warnings = [] } = check;
  if (!suggestion && !reason && !flags.length && !warnings.length) return null;
  return (
    <div className="mt-3 space-y-2" data-testid="ricetta-post-cooking-suggestion">
      {(suggestion || reason) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-xs leading-relaxed text-amber-950">
          <div className="flex items-center gap-1.5 font-semibold"><Lightbulb className="h-4 w-4 text-amber-600" /> Suggerimento del software{suggestion ? `: ${POST_COOKING_LABELS[suggestion]}` : ""}</div>
          {reason && <p className="mt-1">{reason}</p>}
          <p className="mt-1 text-[11px] text-amber-800">È un supporto alla decisione, non una valutazione di conformità: la scelta e la sua motivazione restano del consulente.</p>
          {suggestion && value !== suggestion && onUse && <button type="button" onClick={() => onUse(suggestion)} className="mt-2 min-h-[36px] rounded-lg border border-amber-300 bg-white px-3 text-xs font-semibold text-amber-900 hover:bg-amber-100">Usa la modalità suggerita</button>}
        </div>
      )}
      {flags.length > 0 && (
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-xs text-orange-950">
          <div className="font-semibold">Componenti da valutare nel prodotto finito</div>
          <ul className="mt-1 space-y-0.5">{flags.map((f) => <li key={f.code}>• {f.text}</li>)}</ul>
        </div>
      )}
      {warnings.map((w) => (
        <div key={w} role="alert" className="flex items-start gap-2 rounded-xl border-2 border-red-300 bg-red-50 p-3 text-xs font-medium leading-relaxed text-red-800"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{w}</div>
      ))}
    </div>
  );
}

// Parametri della modalità, valutazione HACCP e fasi successive.
export function PostCookingDetails({ mode, process, onProcess, assessment, onAssessment, phases, onPhases, testPrefix = "ricetta" }) {
  const def = MODE_BY_KEY[mode];
  if (!def) return null;
  const needsAssessment = !!def.assessment;
  const motivationMissing = needsAssessment && !(assessment?.motivation || "").trim();
  return (
    <div className="mt-3" data-testid={`${testPrefix}-post-cooking-details`}>
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3 text-xs leading-relaxed text-indigo-950">
        <div className="font-semibold">Registrazioni pertinenti con questa modalità</div>
        <ul className="mt-1 space-y-0.5">{def.records.map((r) => <li key={r}>• {r}</li>)}</ul>
        <p className="mt-1.5 text-[11px] text-indigo-800">Abbattimento in produzione: <strong>{modeNeedsBlast(mode, process) ? "proposto" : "non previsto"}</strong>. Le verifiche delle corrette prassi igieniche (GHP) non sono CCP: un controllo diventa CCP solo se lo stabilisce l’analisi dei pericoli.</p>
      </div>
      <Collapsible title={`Parametri: ${def.label}`} badge={<OptionalTag />} defaultOpen={mode === "ambient" || mode === "custom"} testId={`${testPrefix}-post-cooking-params`}>
        <p className="mb-3 text-xs text-slate-500">Compila solo i parametri pertinenti alla tua valutazione HACCP. Nessun valore è imposto dal software.</p>
        <FieldGrid fields={def.fields} values={process} onChange={onProcess} testPrefix={`${testPrefix}-param`} />
        {mode === "ambient" && !process?.procedure_text && (
          <button type="button" onClick={() => onProcess({ ...(process || {}), procedure_text: AMBIENT_PROCEDURE_TEXT })} className="mt-3 min-h-[36px] rounded-lg border border-indigo-200 bg-white px-3 text-xs font-semibold text-indigo-800 hover:bg-indigo-50" data-testid={`${testPrefix}-ambient-text`}>Inserisci il testo proposto per la procedura</button>
        )}
        {mode === "ambient" && process?.procedure_text && <p className="mt-2 text-[11px] text-slate-500">Testo da approvare e adattare: non dichiara una valutazione che non risulti documentata nella sezione qui sotto.</p>}
      </Collapsible>
      <Collapsible
        title="Valutazione HACCP della modalità"
        badge={needsAssessment ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${motivationMissing ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{motivationMissing ? "Richiesta per approvare" : "Documentata"}</span> : <OptionalTag />}
        defaultOpen={needsAssessment}
        testId={`${testPrefix}-assessment`}
      >
        {assessment?.mode && assessment.mode !== mode && (assessment.motivation || "").trim() && <p className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs font-medium text-amber-900" data-testid={`${testPrefix}-assessment-other-mode`}>Questa valutazione era stata scritta per «{POST_COOKING_LABELS[assessment.mode]}»: rivedila per la nuova modalità prima di salvare.</p>}
        <p className="mb-3 text-xs text-slate-500">{needsAssessment ? "Per questa modalità la ricetta si può salvare in bozza, ma per approvarla serve la motivazione della scelta." : "Facoltativa: documenta chi ha valutato la modalità e su quali basi."}</p>
        <FieldGrid fields={ASSESSMENT_FIELDS} values={assessment} onChange={onAssessment} testPrefix={`${testPrefix}-assessment`} />
      </Collapsible>
      {onPhases && <PhasesEditor phases={phases || []} onChange={onPhases} testPrefix={testPrefix} />}
    </div>
  );
}

// Processi in più fasi (es. raffreddamento → refrigerazione → rigenerazione
// → mantenimento a caldo): elenco ordinato, senza motore di workflow.
function PhasesEditor({ phases, onChange, testPrefix }) {
  const update = (i, value) => onChange(phases.map((p, idx) => (idx === i ? value : p)));
  const move = (i, dir) => { const next = [...phases]; [next[i], next[i + dir]] = [next[i + dir], next[i]]; onChange(next); };
  return (
    <Collapsible title={`Fasi successive del processo${phases.length ? ` (${phases.length})` : ""}`} badge={<OptionalTag />} defaultOpen={phases.length > 0} testId={`${testPrefix}-phases`}>
      <p className="mb-3 text-xs text-slate-500">Per prodotti che attraversano più condizioni dopo la modalità scelta, es. refrigerazione → rigenerazione → mantenimento a caldo. Indica le fasi nell’ordine in cui avvengono.</p>
      <div className="space-y-3">
        {phases.map((p, i) => (
          <div key={i} className="rounded-xl border border-hairline bg-slate-50/60 p-3" data-testid={`${testPrefix}-phase-${i}`}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fase {i + 1}</span>
              <span className="flex gap-1">
                <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:text-navy disabled:opacity-30" aria-label="Sposta su"><ArrowUp className="h-4 w-4" /></button>
                <button type="button" disabled={i === phases.length - 1} onClick={() => move(i, 1)} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:text-navy disabled:opacity-30" aria-label="Sposta giù"><ArrowDown className="h-4 w-4" /></button>
                <button type="button" onClick={() => onChange(phases.filter((_, idx) => idx !== i))} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:text-red-600" aria-label="Elimina fase"><Trash2 className="h-4 w-4" /></button>
              </span>
            </div>
            <FieldGrid fields={PHASE_FIELDS} values={p} onChange={(v) => update(i, v)} testPrefix={`${testPrefix}-phase-${i}`} />
          </div>
        ))}
      </div>
      {phases.length < 20 && <button type="button" onClick={() => onChange([...phases, { name: "" }])} className="mt-3 inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 text-xs font-semibold text-navy hover:bg-slate-50" data-testid={`${testPrefix}-phase-add`}><Plus className="h-4 w-4" /> Aggiungi fase</button>}
    </Collapsible>
  );
}

// Prodotti non cotti in questa fase: stato e condizioni (impasti, lievito
// madre, semilavorati). Nessun impasto crudo è trattato come pronto al consumo.
export function RawProcessDetails({ value, onChange }) {
  return (
    <Collapsible title="Stato del prodotto non cotto" badge={<OptionalTag />} defaultOpen={!!value?.state} testId="ricetta-raw-process">
      <p className="mb-3 text-xs text-slate-500">Per impasti, lievito madre e semilavorati destinati a una lavorazione successiva: la sezione «dopo la cottura» non si applica.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {RAW_STATES.map((s) => (
          <button key={s.key} type="button" onClick={() => onChange({ ...(value || {}), state: value?.state === s.key ? "" : s.key })} aria-pressed={value?.state === s.key}
            className={`min-h-[44px] rounded-lg border-2 px-3 py-2 text-left text-xs ${value?.state === s.key ? "border-indigo-500 bg-indigo-50 font-semibold text-indigo-900" : "border-hairline bg-white text-navy hover:border-indigo-300"}`} data-testid={`ricetta-raw-state-${s.key}`}>{s.label}</button>
        ))}
      </div>
      {value?.state === "finished_raw" && <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-800"><Info className="mt-px h-3 w-3 shrink-0" />Usa «prodotto finito non cotto» solo per alimenti destinati al consumo così come sono: un impasto crudo destinato alla cottura è un semilavorato.</p>}
      <div className="mt-3"><FieldGrid fields={RAW_PROCESS_FIELDS} values={value} onChange={onChange} testPrefix="ricetta-raw" /></div>
    </Collapsible>
  );
}

export function ShelfLifeDetails({ value, onChange }) {
  return (
    <Collapsible title="Dettagli della shelf life" badge={<OptionalTag />} defaultOpen={!!(value?.basis || value?.hours)} testId="ricetta-shelf-life-info">
      <p className="mb-3 text-xs leading-relaxed text-slate-500">La durata di conservazione definita dal processo non coincide necessariamente con la data in etichetta. L’esenzione dall’indicazione del TMC (es. panetteria consumata entro 24 ore) non elimina gli obblighi di sicurezza e non significa durata illimitata. Il tipo di data stampato resta quello della scheda etichetta.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Indicazione della data (nota per il consulente)"><Select value={value?.date_indication ?? ""} onChange={(e) => onChange({ ...(value || {}), date_indication: e.target.value })} data-testid="ricetta-shelf-life-date-indication">{DATE_INDICATIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
      </div>
      <div className="mt-3"><FieldGrid fields={SHELF_LIFE_INFO_FIELDS} values={value} onChange={onChange} testPrefix="ricetta-shelf-life-info" /></div>
    </Collapsible>
  );
}

// Cosa cambia passando a un'altra modalità su una ricetta già esistente.
export function ModeChangeImpact({ recipe, mode, process, isCooked, confirmed, onConfirm }) {
  const [impact, setImpact] = useState(null);
  const from = recipe?.is_cooked ? recipe.post_cooking_destination : null;
  const changed = !!recipe && isCooked && mode && from !== mode;
  const blastRecord = !!process?.blast_record;
  useEffect(() => {
    if (!changed) { setImpact(null); return; }
    api.get(`/app/catalog/recipes/${recipe.id}/post-cooking-impact`, { params: { destination: mode, is_cooked: true, blast_record: blastRecord } })
      .then(({ data }) => setImpact(data)).catch(() => setImpact(null));
  }, [changed, recipe?.id, mode, blastRecord]);
  if (!changed) return null;
  const items = [];
  if (impact) {
    if (impact.blast_before !== impact.blast_after) items.push(impact.blast_after ? "Dalla nuova versione in produzione verrà proposta la registrazione del raffreddamento/abbattimento." : "Dalla nuova versione l’abbattimento non verrà più proposto in produzione.");
    if (impact.storage_before !== impact.storage_after) items.push("Cambiano le condizioni di conservazione: verifica la dicitura di conservazione e la shelf life della ricetta.");
    if (impact.label_profile) items.push("Esiste una scheda etichetta per questo prodotto: verifica shelf life, temperatura e tipo di data in Etichette.");
    if (impact.active_schedules) items.push(`${impact.active_schedules} ${impact.active_schedules === 1 ? "programmazione attiva" : "programmazioni attive"}: dopo l’approvazione useranno la nuova versione.`);
    if (impact.pending_blasts) items.push(`${impact.pending_blasts} ${impact.pending_blasts === 1 ? "abbattimento ancora in attesa" : "abbattimenti ancora in attesa"} su produzioni già fatte: restano da completare o gestire, non vengono cancellati.`);
    if (impact.open_nonconformities) items.push(`${impact.open_nonconformities} non conformità aperte sui lotti di questa ricetta.`);
  }
  return (
    <div className="mt-3 rounded-xl border-2 border-sky-300 bg-sky-50 p-3 text-xs leading-relaxed text-sky-950" data-testid="ricetta-mode-change-impact">
      <div className="font-semibold">Cambio di modalità: {from ? POST_COOKING_LABELS[from] : "non indicata"} → {POST_COOKING_LABELS[mode]}</div>
      {!impact ? <p className="mt-1 text-sky-800">Verifica delle sezioni collegate in corso…</p> : (
        <ul className="mt-1 space-y-0.5">{items.length ? items.map((t) => <li key={t}>• {t}</li>) : <li>• Nessuna registrazione o programmazione collegata da rivedere.</li>}</ul>
      )}
      <p className="mt-1.5 text-[11px] text-sky-800">Le produzioni e le registrazioni già fatte restano invariate (storico della versione {recipe.version || 1}); la modifica vale dalla nuova versione, dopo l’approvazione.</p>
      <label className="mt-2 flex min-h-[40px] cursor-pointer items-center gap-2 font-semibold">
        <input type="checkbox" checked={!!confirmed} onChange={(e) => onConfirm(e.target.checked)} className="h-5 w-5 accent-sky-600" data-testid="ricetta-mode-change-confirm" /> Ho letto il riepilogo del cambio di modalità
      </label>
    </div>
  );
}
